import { TAG_MAX_LENGTH, TAG_MAX_PER_ENTITY, TAG_PATTERN } from '@kanto/shared';
import { useId, useState, type KeyboardEvent, type ReactElement } from 'react';
import { useTags } from '../api/hooks';
import { useT } from '../lib/i18n';

/**
 * How a set of tags is written: chips for what is already there, one field for
 * what comes next, and the account's existing vocabulary offered underneath.
 *
 * The completion list is the reason this is a component rather than a text field
 * that splits on commas. A tag is only worth having if the same word is reused —
 * `work` and `wrok` are two tags, and the second one is invisible until a filter
 * quietly returns nothing. Offering what already exists is what makes reuse the
 * path of least effort, and it is the whole return on tags being an entity
 * (D260815).
 *
 * **The rule is checked here as well as on the server**, which is not
 * duplication for its own sake: refused here, a mistyped tag stays in the field
 * to be corrected, while refused by the server it arrives as a 400 that clears
 * the composer and explains nothing.
 */

/** How many existing tags are offered at once. More than a glance is a menu. */
const SUGGESTIONS_SHOWN = 6;

export function TagInput({
  tags,
  onChange,
  disabled = false,
}: {
  tags: string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
}): ReactElement {
  const t = useT();
  const { data } = useTags();
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  /** Which suggestion the arrows are on, or -1 for none. */
  const [highlighted, setHighlighted] = useState(-1);
  const listId = useId();

  const full = tags.length >= TAG_MAX_PER_ENTITY;
  const typed = draft.trim().toLowerCase();
  const malformed = typed !== '' && (typed.length > TAG_MAX_LENGTH || !TAG_PATTERN.test(typed));

  const suggestions = (data?.tags ?? [])
    .map((tag) => tag.name)
    .filter((name) => !tags.includes(name) && name.startsWith(typed))
    .slice(0, SUGGESTIONS_SHOWN);

  /** True when the draft was taken, and therefore when the field may be cleared. */
  const accept = (raw: string): boolean => {
    const name = raw.trim().toLowerCase();
    if (name === '') return false;
    // Already there: the draft has served its purpose even though nothing moves.
    if (tags.includes(name)) return true;
    if (full || name.length > TAG_MAX_LENGTH || !TAG_PATTERN.test(name)) return false;
    onChange([...tags, name]);
    return true;
  };

  const commit = (raw: string): void => {
    if (!accept(raw)) return;
    setDraft('');
    setHighlighted(-1);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter' || event.key === ',') {
      // Without this, Enter submits the composer around this field with the tag
      // still sitting unwritten in it — and a comma is how a list is typed.
      event.preventDefault();
      commit(suggestions[highlighted] ?? draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && tags.length > 0) {
      // The chips are to the left of the caret, so backspace over an empty field
      // takes the last one off. It is the gesture every field of this shape has,
      // and its absence is felt immediately.
      event.preventDefault();
      onChange(tags.slice(0, -1));
      return;
    }
    if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestions.length > 0) {
      event.preventDefault();
      setOpen(true);
      setHighlighted((index) =>
        event.key === 'ArrowDown'
          ? (index + 1) % suggestions.length
          : index <= 0
            ? suggestions.length - 1
            : index - 1,
      );
      return;
    }
    if (event.key === 'Escape') {
      setOpen(false);
      setHighlighted(-1);
    }
  };

  return (
    <div className="relative min-w-40 flex-1">
      <div
        className={`flex flex-wrap items-center gap-1 rounded-lg border bg-paper px-2 py-1 ${
          malformed ? 'border-alert' : 'border-line focus-within:border-accent'
        }`}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="flex items-center gap-1 rounded-full bg-accent-soft py-0.5 pr-1 pl-2 text-xs font-medium text-accent"
          >
            {tag}
            <button
              type="button"
              onClick={() => onChange(tags.filter((entry) => entry !== tag))}
              aria-label={t('tags.remove', tag)}
              className="flex size-4 items-center justify-center rounded-full hover:bg-accent hover:text-accent-ink"
            >
              <svg
                viewBox="0 0 24 24"
                className="size-3"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </span>
        ))}

        <input
          type="text"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setOpen(true);
            setHighlighted(-1);
          }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          // On the way out as well as on Enter: somebody who types a tag and
          // presses the composer's own button meant to add both.
          onBlur={() => {
            setOpen(false);
            commit(draft);
          }}
          placeholder={full ? '' : t('tags.add')}
          aria-label={t('tags.label')}
          disabled={disabled || full}
          role="combobox"
          aria-expanded={open && suggestions.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={highlighted >= 0 ? `${listId}-${highlighted}` : undefined}
          aria-invalid={malformed}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="min-h-8 w-20 min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-ink-400"
        />
      </div>

      {/* The rule, only once it has been broken. Shown before the server sees it,
          because a 400 arrives after the field has been cleared. */}
      {malformed && <p className="mt-1 text-xs text-alert">{t('tags.rule')}</p>}
      {full && !malformed && (
        <p className="mt-1 text-xs text-ink-400">{t('tags.full', TAG_MAX_PER_ENTITY)}</p>
      )}

      {open && suggestions.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label={t('tags.suggestions')}
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-sm"
        >
          {suggestions.map((name, index) => (
            <li
              key={name}
              id={`${listId}-${index}`}
              role="option"
              aria-selected={index === highlighted}
            >
              <button
                type="button"
                // Before the click, so the field never loses focus and `onBlur`
                // never fires: blurring first would close this list out from
                // under the pointer that is landing on it.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commit(name)}
                className={`flex w-full min-h-9 items-center px-3 text-left text-sm ${
                  index === highlighted
                    ? 'bg-accent-soft text-accent'
                    : 'text-ink-700 hover:bg-paper'
                }`}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
